import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import {
  allMonthCalendarQueries,
  readMonthCalendar,
  type MonthCalendarKey,
} from '@opallabs/api/BFF'
import { type IETFLanguageTag } from '@opallabs/concepts'
import { toast } from '@opallabs/lapidary'
import { type CalendarDateChange, type CalendarDropProposal } from '../dnd'
import { endDateOf, rescheduleContent, rescheduleMoment } from './reschedule'
import { RescheduleToast } from './RescheduleToast'

/**
 * Which way round the move is being made. Undo is the same move, negated.
 *
 * Both directions are worked out against the dates the API is holding at the
 * time, not against the dates the calendar was drawing. That keeps the clock
 * time and the length of an in-market window exact, at the cost of one case: if
 * the same item is moved again by someone else before Undo is pressed, Undo
 * steps back from where it is now rather than putting it where it was.
 */
type Direction = 1 | -1

type RescheduleParams = {
  direction: Direction
  proposal: CalendarDropProposal
  /** For an Undo, when the content had no time of its own before the move. */
  shouldClearTime?: boolean
}

/**
 * How much of the move landed. Content dropped past the end of its moment is
 * two writes, and the second can fail after the first has been made: the item
 * has moved, which is neither a success nor something to try again.
 */
type RescheduleOutcome = 'moved' | 'moved-without-stretch'

type RescheduleResult = {
  outcome: RescheduleOutcome
  /**
   * The move gives content with no time of its own a time of midnight, so
   * Undo has to take that time away again rather than move it back.
   */
  shouldClearTimeOnUndo: boolean
}

const applyChange = async (
  change: CalendarDateChange,
  days: number,
  {
    endDate,
    shouldClearTime,
  }: { endDate?: string | null; shouldClearTime?: boolean } = {}
): Promise<{ hadScheduledTime?: boolean }> => {
  if (change.kind === 'content') {
    return rescheduleContent({ contentId: change.id, days, shouldClearTime })
  }

  await rescheduleMoment({ days, endDate, momentId: change.id })
  return {}
}

/**
 * Moves what a person dropped, and puts it back when they change their mind.
 *
 * The cache is written before the request goes out so the item stays where it
 * was dropped, and the window is read again once the server has answered — a
 * moment carries its content with it, and only the server knows everything that
 * came along.
 */
export const useRescheduleCalendarItem = ({
  identifiers,
  locale,
}: {
  /** The month window on screen, so its cache can be written into. */
  identifiers: MonthCalendarKey
  locale?: IETFLanguageTag
}) => {
  const queryClient = useQueryClient()

  const { mutate } = useMutation({
    mutationFn: async ({
      direction,
      proposal,
      shouldClearTime,
    }: RescheduleParams): Promise<RescheduleResult> => {
      const { hadScheduledTime } = await applyChange(
        proposal.moved,
        proposal.days * direction,
        { shouldClearTime }
      )
      const shouldClearTimeOnUndo = hadScheduledTime === false

      if (proposal.parent === undefined)
        return { outcome: 'moved', shouldClearTimeOnUndo }
      // The stretch is not a move: the moment stays where it is and only its
      // end changes, so undoing it is a matter of putting the old end back.
      const parent =
        direction === 1 ? proposal.parent.next : proposal.parent.previous

      try {
        await applyChange(parent, 0, { endDate: endDateOf(parent) })
      } catch {
        return { outcome: 'moved-without-stretch', shouldClearTimeOnUndo }
      }

      return { outcome: 'moved', shouldClearTimeOnUndo }
    },

    // Undo is a move as well, and can fail the same way.
    onError: () => {
      toast('That could not be moved. Please try again.', { type: 'error' })
    },

    onMutate: ({ direction, proposal }) => {
      const moved = direction === 1 ? proposal.moved : proposal.movedFrom
      const parentMoment =
        direction === 1 ? proposal.parent?.next : proposal.parent?.previous

      readMonthCalendar.setQueryData(queryClient, identifiers, (draft) => {
        let next =
          moved.kind === 'moment'
            ? readMonthCalendar.transformCache.moveMoment(moved.id, {
                endDate: endDateOf(moved),
                startDate: moved.start,
              })(draft)
            : readMonthCalendar.transformCache.moveContent(moved.id, {
                scheduledAt: moved.startsAt ?? null,
                scheduledDate: moved.start,
                takedownDate: moved.inMarket?.end ?? null,
              })(draft)

        if (parentMoment !== undefined) {
          next = readMonthCalendar.transformCache.moveMoment(parentMoment.id, {
            endDate: endDateOf(parentMoment),
            startDate: parentMoment.start,
          })(next)
        }

        return next
      })
    },

    onSettled: () => {
      allMonthCalendarQueries.invalidateCache(queryClient, {
        workspaceUuid: identifiers.workspaceUuid,
      })
    },
  })

  return useCallback(
    (proposal: CalendarDropProposal) => {
      mutate(
        { direction: 1, proposal },
        {
          onSuccess: ({ outcome, shouldClearTimeOnUndo }) => {
            const landedOn = new Intl.DateTimeFormat(locale, {
              day: 'numeric',
              month: 'long',
            }).format(new Date(`${proposal.moved.start}T00:00:00`))

            const hasUnstretchedMoment = outcome === 'moved-without-stretch'
            // Undo puts back what was written, and the stretch never was, so
            // the moment is dropped from the move being reversed.
            const toUndo = hasUnstretchedMoment
              ? { ...proposal, parent: undefined }
              : proposal

            toast(
              (toastProps) => (
                <RescheduleToast
                  {...toastProps}
                  hasUnstretchedMoment={hasUnstretchedMoment}
                  title={proposal.moved.title}
                  to={landedOn}
                  onUndo={() =>
                    mutate(
                      {
                        direction: -1,
                        proposal: toUndo,
                        shouldClearTime: shouldClearTimeOnUndo,
                      },
                      {
                        onSuccess: ({ outcome: undone }) => {
                          if (undone !== 'moved-without-stretch') return
                          toast(
                            `"${proposal.moved.title}" moved back, but its moment could not be shortened again.`,
                            { type: 'warning' }
                          )
                        },
                      }
                    )
                  }
                />
              ),
              {
                autoClose: 8000,
                type: hasUnstretchedMoment ? 'warning' : 'success',
              }
            )
          },
        }
      )
    },
    [locale, mutate]
  )
}
